import React, { useEffect, useState, useRef } from "react"
import {
  IonHeader,
  IonContent,
  IonToolbar,
  IonTitle,
  IonPage,
  IonButtons,
  IonBackButton,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  isPlatform,
  IonLoading,
} from "@ionic/react"
import { useDispatch, useSelector } from "react-redux"
import {
  DOCUMENTS_CLEAR,
  getDocuments,
  searchDocuments,
  IS_LOADING,
  DOCUMENT_GET_SUCCESS,
  DOCUMENT_GET_FAILURE,
  IS_LOADING_INFINITE_SCROLL,
} from "../redux/actions"
import { store } from "../index"
import { Filesystem, Directory } from "@capacitor/filesystem"
// import { FileTransfer, FileTransferObject } from "@ionic-native/file-transfer"
import {
  FileTransfer,
  FileTransferObject,
} from "@awesome-cordova-plugins/file-transfer"
import {
  DocumentViewer,
  DocumentViewerOptions,
} from "@awesome-cordova-plugins/document-viewer"
import { File } from "@awesome-cordova-plugins/file"
import "./Documents.scss"
import { AppState, Document } from "../global"
import { FileOpener } from "@capacitor-community/file-opener"
import APIHelper, { DocumentGroup } from "../helpers/API"
import { isCordovaAvailable } from "../helpers/platform"
import storageHelper from "../helpers/storageHelper"

const Documents: React.FC = () => {
  const [showLoading, setShowLoading] = useState(false)

  const infiniteScroll = useRef<HTMLIonInfiniteScrollElement>(null)
  const currentContent = useRef<HTMLIonContentElement>(null)
  const searchBar = useRef<HTMLIonSearchbarElement>(null)

  const [documentGroups, setDocumentGroups] = useState<DocumentGroup[]>([])
  const [currentType, setCurrentType] = useState("")
  // const [path, setPath] = useState<string>("");
  const [selectedDocument, setSelectedDocument] = useState<Document | null>()
  const [searchActive, setsearchActive] = useState<boolean>(false)
  const [pdfLocalUrl, setPdfLocalUrl] = useState<string>()
  const docs = useSelector((state: AppState) => state.documents)
  const nextLink = useSelector((state: AppState) => state.nextPageUrl)
  const isLoading = useSelector((state: AppState) => state.isLoading)
  const isLoadingInfiniteScroll = useSelector(
    (state: AppState) => state.isLoadingInfiniteScroll
  )

  const dispatch = useDispatch()

  useEffect(() => {
    selectDocHandler()
  }, [selectedDocument])

  useEffect(() => {
    console.log("currentType: ", currentType)
    if (!currentType) {
      return
    }

    const searchText = (searchBar.current?.value || "").toString().trim()
    clearDocuments()
    if (searchText.length > 0) {
      setsearchActive(true)
      infiniteScroll.current && (infiniteScroll.current!.disabled = true)
      dispatch(searchDocuments(searchText, currentType))
      currentContent.current?.scrollToTop()
      return
    }

    setsearchActive(false)
    loadDocsScrollToTop()
  }, [currentType])

  useEffect(() => {
    let isMounted = true

    APIHelper.getDocumentGroups()
      .then((groups) => {
        if (!isMounted) {
          return
        }
        setDocumentGroups(groups)
        if (groups.length > 0) {
          // El primo gruppo arriva dal backend: niente tab hardcoded in app.
          setCurrentType((existingType) => existingType || groups[0].name)
        }
      })
      .catch((error) => {
        console.log("document groups error:", error)
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    return () => {
      clearDocuments()
    }
  }, [])

  useEffect(() => {
    if (!isLoadingInfiniteScroll) {
      infiniteScroll.current && infiniteScroll.current.complete()
    }
  }, [isLoadingInfiniteScroll])

  const loadDocsScrollToTop = () => {
    dispatch({ type: IS_LOADING })
    _getDocuments(null)
    currentContent.current?.scrollToTop()
  }

  const _getDocuments = (e: CustomEvent | null) => {
    infiniteScroll.current && (infiniteScroll.current!.disabled = false)
    //if (nextLink != null) {
    dispatch({ type: IS_LOADING_INFINITE_SCROLL })
    dispatch(getDocuments(nextLink, currentType))
    //}
  }

  const selectTypeHandler = (event: CustomEvent) => {
    const selectedType = event.detail.value as string | undefined
    if (!selectedType) {
      return
    }

    clearDocuments()
    setCurrentType(selectedType)
  }

  const clearDocuments = () => {
    store.dispatch({ type: DOCUMENTS_CLEAR })
  }

  const searchDocs = () => {
    const searchText = (searchBar.current?.value || "").toString().trim()
    if (searchText.length > 0) {
      setsearchActive(true)
      infiniteScroll.current && (infiniteScroll.current!.disabled = true)
      dispatch(searchDocuments(searchText, currentType))
    } else {
      setsearchActive(false)
      loadDocsScrollToTop()
    }
  }

  const focusSearchHandler = () => {
    const searchText = (searchBar.current?.value || "").toString().trim()
    if (searchText.length > 0) {
      setsearchActive(true)
      infiniteScroll.current && (infiniteScroll.current!.disabled = true)
    }
  }

  const cancelSearchHandler = () => {
    if (searchBar.current) {
      searchBar.current.value = ""
    }
    setsearchActive(false)
    loadDocsScrollToTop()
  }

  const clearSearchHandler = () => {
    setsearchActive(false)
    loadDocsScrollToTop()
  }

  const selectDocHandler = async () => {
    await downloadFile()
  }

  const openDocument = async (document: Document) => {
    if (!isCordovaAvailable()) {
      // Aprimo subito una scheda nel gesto utente: cussì Safari no la blocca.
      const previewWindow = window.open("", "_blank")
      try {
        const blobUrl = await APIHelper.getDocumentBlobUrl(document.file)
        if (previewWindow) {
          // La PWA resta viva sotto: chiudendo il PDF no ricarica la lista, ostrega.
          previewWindow.location.href = blobUrl
        } else {
          // Fallback raro: se il browser blocca la scheda, almeno il PDF si apre.
          window.location.assign(blobUrl)
        }
      } catch (error) {
        previewWindow?.close()
        alert(
          `Si è verificato un errore scaricando il file. Error: ${JSON.stringify(
            error
          )}`
        )
      }
      return
    }

    setSelectedDocument(document)
  }

  const removeFile = async () => {
    if (selectedDocument) {
      console.log("REMOVE FILE: path", `${selectedDocument!.uuid}.pdf`)
      console.log("REMOVE FILE: directory", Directory.Documents)

      File.removeFile(path, `${selectedDocument!.uuid}.pdf`)
    }
  }

  const path = isPlatform("ios")
    ? File.cacheDirectory
    : File.externalCacheDirectory

  const downloadFile = async () => {
    if (selectedDocument) {
      // In PWA no sta far el furbo: i plugin Cordova non esistono.
      if (!isCordovaAvailable()) {
        const blobUrl = await APIHelper.getDocumentBlobUrl(selectedDocument.file)
        window.location.assign(blobUrl)
        setSelectedDocument(null)
        return
      }

      dispatch({ type: IS_LOADING })
      // store.dispatch({ type: DOCUMENT_DOWNLOADING })
      // setShowLoading(true)
      try {
        console.log("path: ", path)
        console.log("File.documentsDirectory: ", File.dataDirectory)
        const fileTransfer: FileTransferObject = FileTransfer.create()
        const entry = await fileTransfer.download(
          selectedDocument.file,
          `${path}${selectedDocument.uuid}.pdf`,
          false,
          {
            headers: {
              Authorization: `JWT ${storageHelper.getAccessToken()}`,
            },
          }
        )
        const onMissingApp = (appId: any, installer: any) => {
          const url = entry.toURL()
          // alert("opening: " + url)
          FileOpener.open({ filePath: url })
        }
        const options: DocumentViewerOptions = {
          title: document.title,
          print: { enabled: isPlatform("ios") },
          search: { enabled: true },
          openWith: { enabled: isPlatform("ios") },
          bookmarks: { enabled: true },
          email: { enabled: true },
          autoClose: { onPause: false },
          navigationView: { closeLabel: "Chiudi" },
          documentView: { closeLabel: "Chiudi" },
        }

        DocumentViewer.viewDocument(
          entry.nativeURL,
          "application/pdf",
          options,
          () => {},
          () => {
            removeFile()
            setSelectedDocument(null)
            // setShowLoading(false)
          },
          onMissingApp,
          onPdfError
        )
        dispatch({ type: DOCUMENT_GET_SUCCESS })
      } catch (error) {
        console.error(error)
        // setShowLoading(false)
        alert(
          `Si è verificato un errore scaricando il file. Error: ${JSON.stringify(
            error
          )}`
        )
        dispatch({ type: DOCUMENT_GET_FAILURE })
      }
    }
  }

  const onPdfError = (error: any) => {
    alert("Si è verificato un errore: " + error.message)
    console.log("Error" + error)
  }

  return (
    <IonPage>
      <IonLoading cssClass="my-custom-class" isOpen={isLoading} message={""} />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {/*/ if the page is reloaded, there's no history to know what page the back button has to load; 
            defaultHreftells define it */}
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonTitle>Documenti</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment
            value={currentType}
            onIonChange={selectTypeHandler}
            class="is-segment-bordered"
            mode="md"
          >
            {documentGroups.map((group) => (
              <IonSegmentButton mode="md" value={group.name} key={group.id}>
                <IonLabel>{group.name}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>
        </IonToolbar>{" "}
        <IonSearchbar
          placeholder="Cerca"
          cancelButtonText="Annulla"
          ref={searchBar}
          showCancelButton="focus"
          onIonInput={() => searchDocs()}
          onIonFocus={focusSearchHandler}
          onIonCancel={cancelSearchHandler}
          onIonClear={clearSearchHandler}
        >
          {" "}
        </IonSearchbar>
      </IonHeader>

      <IonContent scrollEvents={true} ref={currentContent}>
        {docs && (
          <React.Fragment>
            <IonList id="list">
              {docs.map((document, index) => (
                <IonItem
                  key={document.uuid}
                  button
                  onClick={() => {
                    openDocument(document)
                  }}
                >
                  <IonLabel className="ion-text-wrap">
                    {document.title}
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
            {!searchActive && (
              <IonInfiniteScroll
                threshold="15%"
                onIonInfinite={(e: CustomEvent<void>) => _getDocuments(e)}
                disabled={nextLink == null}
                ref={infiniteScroll}
              >
                <IonInfiniteScrollContent
                  loadingSpinner="bubbles"
                  loadingText="Caricamento dati..."
                ></IonInfiniteScrollContent>
              </IonInfiniteScroll>
            )}
          </React.Fragment>
        )}
      </IonContent>
    </IonPage>
  )
}

export default Documents
